import { useNavigate } from "@tanstack/react-router";
import { BellIcon, CheckIcon, CircleCheckBigIcon, ExternalLinkIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Decision, DecideDialog } from "@/approvals/DecideDialog";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/dialog";
import { SimpleTooltip } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { ago, type NotificationItem, type TodoItem, useMarkSeen, useNotifications, utc } from "./api";

/**
 * Top-bar bell and the notifications pane. The badge counts to-dos (things to act on; they stay until done)
 * plus unread notifications (read-only; opening the pane marks them read).
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { data } = useNotifications();
  const todos = data?.todos.length ?? 0;
  const badge = todos + (data?.unread ?? 0);
  const parts = [todos ? `${todos} to do` : null, data?.unread ? `${data.unread} unread` : null].filter(Boolean);
  const label = parts.length ? `Notifications, ${parts.join(", ")}` : "Notifications";

  return (
    <>
      <SimpleTooltip label="Notifications">
        <Button variant="ghost" size="icon-sm" aria-label={label} className="relative" onClick={() => setOpen(true)}>
          <BellIcon />
          {badge > 0 && (
            <span
              aria-hidden
              className={cn(
                "absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold tabular-nums",
                todos ? "bg-warning text-warning-foreground" : "bg-primary text-primary-foreground",
              )}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </Button>
      </SimpleTooltip>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="gap-0 p-0" aria-describedby={undefined}>
          {open && <NotificationsPane onNavigate={() => setOpen(false)} />}
        </SheetContent>
      </Sheet>
    </>
  );
}

function NotificationsPane({ onNavigate }: { onNavigate: () => void }) {
  const { data, isPending, isError } = useNotifications();
  const markSeen = useMarkSeen();
  const navigate = useNavigate();
  const [deciding, setDeciding] = useState<Decision | null>(null);
  // Unread when the pane opened: shown as "new" for this visit, then marked read on the server.
  const fresh = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data || fresh.current) return;
    const unread = data.items.filter((n) => !n.read_at).map((n) => n.id);
    fresh.current = new Set(unread);
    if (unread.length) markSeen.mutate(unread);
  }, [data, markSeen]);

  const go = (link: string | null) => {
    if (!link) return;
    onNavigate();
    // Links can carry a query string (e.g. "/portal/tickets?ticket=…"): the router wants it as `search`.
    const url = new URL(link, window.location.origin);
    void navigate({ to: url.pathname, search: Object.fromEntries(url.searchParams) });
  };

  return (
    <>
      <SheetHeader>
        <SheetTitle>Notifications</SheetTitle>
        <SheetDescription>Things waiting for you, and what's happened.</SheetDescription>
      </SheetHeader>
      <SheetBody className="flex flex-col gap-6 px-0 py-4">
        {isPending ? (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : isError ? (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground">Couldn't load notifications. Try again shortly.</p>
        ) : (
          <>
            <section aria-labelledby="todo-heading" className="flex flex-col gap-2">
              <h2 id="todo-heading" className="flex items-center gap-2 px-5 text-sm font-semibold">
                To do
                {data.todos.length > 0 && (
                  <span className="rounded-full bg-warning/15 px-2 text-xs font-medium text-warning tabular-nums">{data.todos.length}</span>
                )}
              </h2>
              {data.todos.length === 0 ? (
                <p className="flex items-center gap-2 px-5 text-sm text-muted-foreground">
                  <CircleCheckBigIcon className="size-4 text-success" aria-hidden /> Nothing waiting for you.
                </p>
              ) : (
                <ul className="flex flex-col gap-2 px-3" aria-label="To do">
                  {data.todos.map((t) => (
                    <TodoRow key={t.id} todo={t} onDecide={setDeciding} onOpen={() => go(t.link)} />
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="notifications-heading" className="flex flex-col gap-2">
              <h2 id="notifications-heading" className="px-5 text-sm font-semibold">
                Notifications
              </h2>
              {data.items.length === 0 ? (
                <EmptyState variant="plain" icon={BellIcon} title="You're all caught up" description="Updates about your requests and jobs appear here." className="py-6" />
              ) : (
                <ul className="divide-y border-y" aria-label="Notifications">
                  {data.items.map((n) => (
                    <NotificationRow key={n.id} item={n} isNew={!!fresh.current?.has(n.id)} onOpen={() => go(n.link)} />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </SheetBody>
      {deciding && <DecideDialog decision={deciding} onClose={() => setDeciding(null)} />}
    </>
  );
}

function TodoRow({ todo, onDecide, onOpen }: { todo: TodoItem; onDecide: (d: Decision) => void; onOpen: () => void }) {
  const decision = (d: Decision["decision"]): Decision => ({ id: todo.id, title: todo.title, requestedBy: todo.requested_by, decision: d });
  return (
    <li className="rounded-lg border bg-card p-3">
      <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{todo.label} · approval</p>
      <p className="mt-0.5 text-sm font-medium break-words">{todo.title}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        From {todo.requested_by ?? "someone"} ·{" "}
        <time dateTime={todo.created_at} title={utc(todo.created_at).toLocaleString("en-GB")}>
          {ago(todo.created_at)}
        </time>
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => onDecide(decision("approve"))}>
          <CheckIcon /> Approve
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDecide(decision("reject"))}>
          <XIcon /> Reject
        </Button>
        {todo.link && (
          <Button size="sm" variant="ghost" onClick={onOpen}>
            <ExternalLinkIcon /> Open
          </Button>
        )}
      </div>
    </li>
  );
}

function NotificationRow({ item, isNew, onOpen }: { item: NotificationItem; isNew: boolean; onOpen: () => void }) {
  const body = (
    <>
      <span aria-hidden className={cn("mt-1.5 size-2 shrink-0 rounded-full", isNew ? "bg-primary" : "bg-transparent")} />
      <span className="min-w-0 flex-1">
        <span className={cn("block break-words", isNew && "font-medium")}>{item.title}</span>
        {item.body && <span className="mt-0.5 line-clamp-3 block text-muted-foreground">{item.body}</span>}
        <time dateTime={item.created_at} title={utc(item.created_at).toLocaleString("en-GB")} className="mt-1 block text-xs text-muted-foreground">
          {ago(item.created_at)}
          {isNew && <span className="sr-only"> (new)</span>}
        </time>
      </span>
    </>
  );
  const cls = cn("flex w-full gap-3 px-5 py-3 text-left text-sm", isNew && "bg-accent/40");
  return (
    <li>
      {item.link ? (
        <button type="button" onClick={onOpen} className={cn(cls, "transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none")}>
          {body}
        </button>
      ) : (
        <div className={cls}>{body}</div>
      )}
    </li>
  );
}
